export class CartPage {
  constructor() {
    this.container = null;
    
    // Debounce timers for each item
    this.debounceTimers = new Map();
    // Track pending quantity changes for each item
    this.pendingChanges = new Map();
    
    // Auto-update functionality
    this.isUpdating = false;
    this.updateTimeout = null;
    
    this.init();
  }

  init() {
    this.container = document.querySelector('[data-cart-page]');
    if (!this.container) {
      console.log('CartPage: No cart page found, skipping initialization');
      return;
    }

    // Check if unified cart system is available
    if (!window.cartManager) {
      console.warn('CartPage: Unified cart system not available, quantity buttons may not work');
    } else {
      console.log('CartPage: Unified cart system available, initializing quantity controls');
      
      // Disable mini-cart when on cart page to prevent conflicts
      this.disableMiniCart();
    }

    this.bindEvents();
    
    console.log('CartPage: Initialized on cart page - simple refresh mode');
  }

  /**
   * Disable mini-cart functionality when on cart page to prevent conflicts
   */
  disableMiniCart() {
    if (window.cartDrawer) {
      console.log('CartPage: Disabling cart drawer on cart page');
      window.cartDrawer.disable();
    }
    
    if (Array.isArray(window.miniCarts)) {
      console.log('CartPage: Disabling all mini carts on cart page');
      window.miniCarts.forEach((miniCart) => miniCart?.disable?.());
    } else if (window.miniCart) {
      console.log('CartPage: Disabling mini cart on cart page');
      window.miniCart.disable();
    }
    
    // Hide any mini-cart elements
    const miniCartElements = document.querySelectorAll('[data-mini-cart]');
    miniCartElements.forEach(element => {
      element.style.display = 'none';
    });
  }

  bindEvents() {
    // Handle quantity buttons
    this.container.addEventListener('click', (event) => {
      this.handleCartActions(event);
    });
    
    // Listen for cart events
    if (window.cartManager) {
      window.cartManager.on('cart:emptied', () => {
        console.log('CartPage: Cart emptied event received, updating UI');
        this.handleEmptyCart();
      });
    }
  }

  handleCartActions(event) {
    const target = event.target;
    
    // Find the closest quantity button (handles clicks on child elements)
    const quantityButton = target.closest('[data-quantity-minus], [data-quantity-plus]');
    
    if (!quantityButton) return;
    
    // Skip if button is disabled (linked items with _for property)
    if (quantityButton.disabled) {
      return;
    }

    // Prevent default to avoid any unwanted behavior
    event.preventDefault();
    event.stopPropagation();

    const itemKey = quantityButton.dataset.itemKey;
    const isIncrease = quantityButton.matches('[data-quantity-plus]');

    if (!itemKey) {
      console.warn('CartPage: Quantity button missing item key', quantityButton);
      return;
    }

    // Check if button is disabled
    if (quantityButton.disabled) {
      console.log('CartPage: Quantity button is disabled, ignoring click');
      return;
    }

    console.log('CartPage: Handling quantity button click', { itemKey, isIncrease });

    // Use the unified cart system if available
    if (window.cartManager) {
      this.handleQuantityButtonClick(itemKey, isIncrease);
    } else {
      console.warn('CartPage: Unified cart system not available');
    }
  }

  handleQuantityButtonClick(itemKey, isIncrease) {
    console.log('CartPage: Processing quantity button click', { itemKey, isIncrease });
    
    // Update pending changes
    const currentPending = this.pendingChanges.get(itemKey) || 0;
    const newPending = currentPending + (isIncrease ? 1 : -1);
    
    this.pendingChanges.set(itemKey, newPending);
    
    // Clear existing timer for this item
    if (this.debounceTimers.has(itemKey)) {
      clearTimeout(this.debounceTimers.get(itemKey));
    }
    
    // Set new timer - just refresh page after 600ms
    const timer = setTimeout(() => {
      this.refreshCartPage(itemKey);
    }, 600);
    
    this.debounceTimers.set(itemKey, timer);
    
    console.log('CartPage: Quantity update scheduled', { itemKey, newPending });
  }

  async refreshCartPage(itemKey) {
    console.log('CartPage: Updating cart for:', itemKey);
    
    try {
      const cartManager = window.cartManager;
      const pendingChange = this.pendingChanges.get(itemKey) || 0;
      this.items = Array.from(this.container.querySelectorAll('.cart-page__item'));
      
      console.log("Checking for items");
      console.log(this.items);
      
      const item = this.items.find(item => item.dataset.itemKey === itemKey);
      
      if (!item) {
        console.warn('CartPage: Item not found in cart', itemKey);
        return;
      }
      
      // Read quantity from data-original-quantity attribute on the input element
      const quantityInput = item.querySelector('[data-original-quantity]');
      if (!quantityInput) {
        console.warn('CartPage: Quantity input not found for item', itemKey);
        return;
      }
      item.quantity = parseInt(quantityInput.getAttribute('data-original-quantity')) || parseInt(quantityInput.value) || 0;
      const quantityTexts = item.querySelectorAll('.quantity-btn__text');
      const quantityLoaders = item.querySelectorAll('.quantity-btn__loading');
      const newQuantity = Math.max(0, item.quantity + pendingChange);
      
      // Show loading state
      quantityTexts.forEach((text) => {
        text.classList.add('hidden');
      });
      quantityLoaders.forEach((loader) => {
        loader.classList.remove('hidden');
      });
      
      // Remove item if quantity is 0 or less (e.g., when clicking minus on quantity 1)
      if (newQuantity <= 0) {
        // Remove item if quantity is 0
        console.log('CartPage: Removing item with quantity 0', itemKey, { currentQuantity: item.quantity, pendingChange, newQuantity });
        await cartManager.removeFromCart(parseInt(item.dataset.variant));
      } else {
        // Update quantity
        console.log('CartPage: Updating item quantity', { itemKey, currentQuantity: item.quantity, newQuantity });
        console.log(item.dataset.variant);
        await cartManager.changeItemQuantity(parseInt(item.dataset.variant), newQuantity);
      }
      
      console.log('CartPage: Cart updated successfully, auto-updating page...');
      
      // Clear pending changes for this item
      this.pendingChanges.delete(itemKey);
      this.debounceTimers.delete(itemKey);
      
      // Auto-update the cart page instead of reloading
      await this.autoUpdateCartPage();
      
    } catch (error) {
      console.error('CartPage: Cart update failed:', error);
      
      // Clear pending changes even if update failed
      this.pendingChanges.delete(itemKey);
      this.debounceTimers.delete(itemKey);
      
      // Hide loading state on error
      this.hideLoadingState(itemKey);
    }
  }

  /**
   * Hide loading state for a specific item
   */
  hideLoadingState(itemKey) {
    const item = this.items.find(item => item.dataset.itemKey === itemKey);
    
    if (!item) {
      console.warn('CartPage: Item not found for hiding loading state', itemKey);
      return;
    }
    
    const quantityTexts = item.querySelectorAll('.quantity-btn__text');
    const quantityLoaders = item.querySelectorAll('.quantity-btn__loading');
    
    // Hide loading state
    quantityTexts.forEach((text) => {
      text.classList.remove('hidden');
    });
    quantityLoaders.forEach((loader) => {
      loader.classList.add('hidden');
    });
    
    console.log('CartPage: Loading state hidden for item', itemKey);
  }

  /**
   * Auto-update the cart page using the cart data endpoint
   */
  async autoUpdateCartPage() {
    if (this.isUpdating) {
      console.log('CartPage: Update already in progress, skipping');
      return;
    }

    this.isUpdating = true;
    console.log('CartPage: Starting auto-update...');

    try {
      // Fetch fresh cart HTML
      const response = await fetch('/cart?view=data', {
        method: 'GET',
        headers: {
          'Accept': 'text/html',
          'Content-Type': 'text/html'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const cartHTML = await response.text();
      console.log('CartPage: Fetched cart HTML');

      // Parse the new HTML
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = cartHTML;
      const newCartPage = tempDiv.querySelector('[data-cart-page]');
      
      if (newCartPage) {
        // Update only the specific parts that change
        
        // 1. Update cart items container
        const currentItemsContainer = this.container.querySelector('[data-cart-items]');
        const newItemsContainer = newCartPage.querySelector('[data-cart-items]');
        if (currentItemsContainer && newItemsContainer) {
          currentItemsContainer.innerHTML = newItemsContainer.innerHTML;
          console.log('CartPage: Updated cart items');
          
          // Check if cart is now empty and update empty state
          const hasItems = newItemsContainer.querySelector('.cart-page__item');
          const emptyMessage = this.container.querySelector('.cart-page__empty-message');
          
          if (!hasItems && !emptyMessage) {
            // Cart is empty but no empty message exists, add it
            const emptyMessageDiv = document.createElement('div');
            emptyMessageDiv.className = 'cart-page__empty-message empty-cart-message text-center py-8';
            emptyMessageDiv.textContent = 'Your cart is empty';
            currentItemsContainer.appendChild(emptyMessageDiv);
            console.log('CartPage: Added empty cart message');
          } else if (hasItems && emptyMessage) {
            // Cart has items but empty message exists, remove it
            emptyMessage.remove();
            console.log('CartPage: Removed empty cart message');
          }
        }
        
        // 2. Update subtotal
        const currentSubtotal = this.container.querySelector('[data-cart-subtotal]');
        const newSubtotal = newCartPage.querySelector('[data-cart-subtotal]');
        if (currentSubtotal && newSubtotal) {
          currentSubtotal.textContent = newSubtotal.textContent;
          console.log('CartPage: Updated subtotal');
        }
        
        // 3. Update savings line (if it exists)
        const currentSavingsContainer = this.container.querySelector('.cart-summary__pricing-line');
        const newSavingsContainer = newCartPage.querySelector('.cart-summary__pricing-line');
        
        if (newSavingsContainer) {
          // Savings exist in new HTML
          if (currentSavingsContainer) {
            // Replace existing savings
            currentSavingsContainer.innerHTML = newSavingsContainer.innerHTML;
          } else {
            // Insert new savings after subtotal
            const subtotalContainer = this.container.querySelector('.cart-summary__subtotal');
            if (subtotalContainer && subtotalContainer.parentNode) {
              subtotalContainer.parentNode.insertBefore(newSavingsContainer, subtotalContainer.nextSibling);
            }
          }
          console.log('CartPage: Updated savings');
        } else if (currentSavingsContainer) {
          // Remove savings if they don't exist in new HTML
          currentSavingsContainer.remove();
          console.log('CartPage: Removed savings');
        }
        
        // 4. Update cart count
        const currentCount = this.container.querySelector('[data-cart-count]');
        const newCount = newCartPage.querySelector('[data-cart-count]');
        if (currentCount && newCount) {
          currentCount.textContent = newCount.textContent;
          console.log('CartPage: Updated cart count');
        }
        
        // 5. Update checkout button state
        const currentCheckoutBtn = this.container.querySelector('[name="checkout"]');
        const newCheckoutBtn = newCartPage.querySelector('[name="checkout"]');
        if (currentCheckoutBtn && newCheckoutBtn) {
          currentCheckoutBtn.disabled = newCheckoutBtn.disabled;
          console.log('CartPage: Updated checkout button state');
        }
        
        console.log('CartPage: Cart page updated successfully (targeted update)');
      } else {
        throw new Error('No cart page found in response');
      }

    } catch (error) {
      console.error('CartPage: Auto-update failed:', error);
      // Fallback to page reload if auto-update fails
      console.log('CartPage: Falling back to page reload');
      window.location.reload();
    } finally {
      this.isUpdating = false;
    }
  }

  /**
   * Handle empty cart state
   */
  handleEmptyCart() {
    const itemsContainer = this.container.querySelector('[data-cart-items]');
    const emptyMessage = this.container.querySelector('.cart-page__empty-message');
    
    if (itemsContainer && !emptyMessage) {
      // Add empty message
      const emptyMessageDiv = document.createElement('div');
      emptyMessageDiv.className = 'cart-page__empty-message empty-cart-message text-center py-8';
      emptyMessageDiv.textContent = 'Your cart is empty';
      itemsContainer.appendChild(emptyMessageDiv);
      console.log('CartPage: Added empty cart message via event');
    }
    
    // Update any cart state elements
    const cartStateElements = this.container.querySelectorAll('[data-cart-state]');
    cartStateElements.forEach(element => {
      element.setAttribute('data-empty', 'true');
      element.setAttribute('data-item-count', '0');
      element.classList.add('cart-empty');
      element.classList.remove('cart-has-items');
    });
  }

  destroy() {
    // Clear all timers
    this.debounceTimers.forEach(timer => clearTimeout(timer));
    this.debounceTimers.clear();
    this.pendingChanges.clear();
    
    if (this.updateTimeout) {
      clearTimeout(this.updateTimeout);
    }
    
    console.log('CartPage: Destroyed');
  }
}
